import React, { useState, useRef, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { X, Upload } from "lucide-react";
import { cn } from "../../lib/utils";

interface ImageFile {
  file: File;
  preview: string;
  id: string;
}

interface ImageUploadProps {
  label?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in bytes
  acceptedTypes?: string[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  onUpload?: (files: File[]) => Promise<string[]>;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  multiple = false,
  maxFiles = multiple ? 10 : 1,
  maxSize = 5 * 1024 * 1024, // 5MB
  acceptedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"],
  value,
  onChange,
  onUpload,
  error,
  disabled = false,
  className,
}) => {
  const effectiveValue = useMemo(
    () => value ?? (multiple ? [] : ""),
    [value, multiple]
  );
  const [images, setImages] = useState<ImageFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imagesRef = React.useRef<ImageFile[]>([]);

  const pendingOnChangeRef = React.useRef<string | string[] | null>(null);
  const onChangeRef = React.useRef(onChange);

  // No-op for ref tracking
  React.useEffect(() => {
    imagesRef.current = images;
    onChangeRef.current = onChange;
  }, [images, onChange]);

  const lastSentValueRef = React.useRef<string>("");

  // Handle onChange safely after state updates
  React.useEffect(() => {
    if (pendingOnChangeRef.current !== null) {
      const valueToSend = pendingOnChangeRef.current;
      pendingOnChangeRef.current = null;

      const valueJson = JSON.stringify(valueToSend);
      lastSentValueRef.current = valueJson;

      onChangeRef.current?.(valueToSend);
    }
  }, [images]);

  // Helper to create image objects from URLs
  const createImageObjects = useCallback((urls: string[]): ImageFile[] => {
    return urls
      .filter((url) => url && typeof url === "string" && url.trim() !== "")
      .map((url, index) => {
        const filename = url.startsWith("data:")
          ? `image-${index}.${url.match(/data:image\/(\w+)/)?.[1] || "png"}`
          : url.split("/").pop() || url.split("\\").pop() || `image-${index}`;
        return {
          file: new File([], filename),
          preview: url,
          id: `img-${index}-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        };
      });
  }, []);

  // Initialize and sync images from value prop
  React.useEffect(() => {
    const valueJson = JSON.stringify(effectiveValue);

    // Skip if value matches what we last sent (internal update)
    if (valueJson === lastSentValueRef.current) {
      return;
    }

    // Extract URLs from value prop
    let valueUrls: string[] = [];
    if (multiple) {
      valueUrls = Array.isArray(effectiveValue)
        ? effectiveValue.filter((url) => typeof url === "string" && url.trim() !== "")
        : [];
    } else {
      const val = typeof effectiveValue === "string" ? effectiveValue : (Array.isArray(effectiveValue) ? effectiveValue[0] : "");
      valueUrls = (typeof val === "string" && val.trim() !== "") ? [val] : [];
    }

    // Compare with current local images to avoid unnecessary state updates
    const currentUrls = imagesRef.current.map(img => img.preview);
    const hasBlobUrls = currentUrls.some(url => url.startsWith("blob:"));

    // Skip if URLs already match OR if we have blobs and parent is just missing them
    // (This prevents parent state from overwriting temporary local previews during upload)
    const currentUrlsKey = [...currentUrls].sort().join("|");
    const valueUrlsKey = [...valueUrls].sort().join("|");

    if (currentUrlsKey === valueUrlsKey) {
      return;
    }

    // If we have blobs and the incoming value is just the same set minus the blobs, skip it
    if (hasBlobUrls) {
      const currentServerUrlsKey = [...currentUrls].filter(u => !u.startsWith("blob:")).sort().join("|");
      if (currentServerUrlsKey === valueUrlsKey) {
        return;
      }
    }

    // Update internal state to match props
    const newImages = createImageObjects(valueUrls);
    setImages(newImages);
  }, [effectiveValue, multiple, createImageObjects]);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!acceptedTypes.includes(file.type)) {
        return `File type ${file.type
          } is not supported. Supported types: ${acceptedTypes.join(", ")}`;
      }
      if (file.size > maxSize) {
        return `File size ${Math.round(
          file.size / 1024 / 1024
        )}MB exceeds maximum size of ${Math.round(maxSize / 1024 / 1024)}MB`;
      }
      return null;
    },
    [acceptedTypes, maxSize]
  );

  const processFiles = useCallback(
    async (files: File[]) => {
      const validFiles: ImageFile[] = [];
      const errors: string[] = [];

      for (const file of files) {
        const error = validateFile(file);
        if (error) {
          errors.push(error);
        } else {
          const preview = URL.createObjectURL(file);
          validFiles.push({
            file,
            preview,
            id: `new-${Date.now()}-${Math.random()}`,
          });
        }
      }

      if (errors.length > 0) {
        // Handle errors - could emit to parent or show toast
        console.error("File validation errors:", errors);
      }

      if (validFiles.length > 0) {
        // Use functional update to get current images state
        setImages((currentImages) => {
          // Calculate how many existing images we can keep
          const currentImageCount = currentImages.length;
          const remainingSlots = Math.max(0, maxFiles - currentImageCount);
          const filesToAdd = validFiles.slice(0, remainingSlots);

          if (filesToAdd.length === 0) {
            console.warn(`Maximum file limit (${maxFiles}) reached`);
            return currentImages;
          }

          const newImages = multiple
            ? [...currentImages, ...filesToAdd]
            : filesToAdd;
          const limitedImages = newImages.slice(0, maxFiles);

          // Upload if onUpload provided
          if (onUpload) {
            setUploading(true);
            // Upload only the new files
            onUpload(filesToAdd.map((img) => img.file))
              .then((uploadedUrls) => {
                // Create a map of new image IDs to uploaded URLs
                const uploadedMap = new Map<string, string>();
                filesToAdd.forEach((img, idx) => {
                  if (uploadedUrls[idx]) {
                    uploadedMap.set(img.id, uploadedUrls[idx]);
                  }
                });

                // Update images with functional update to ensure we have the latest state
                setImages((prevImages) => {
                  // Find the images that were just added and update them with uploaded URLs
                  const updatedImages = prevImages.map((img) => {
                    const uploadedUrl = uploadedMap.get(img.id);
                    if (uploadedUrl) {
                      // Clean up object URL to prevent memory leaks
                      if (img.preview.startsWith("blob:")) {
                        URL.revokeObjectURL(img.preview);
                      }
                      // Create a new image object with the uploaded URL
                      return {
                        ...img,
                        preview: uploadedUrl,
                        id: `uploaded-${Date.now()}-${Math.random()
                          .toString(36)
                          .substr(2, 9)}`,
                      };
                    }
                    return img;
                  });

                  // Calculate value to send - only call onChange after upload completes
                  // IMPORTANT: Filter out any remaining blob: URLs to avoid leaking temporary previews
                  const urls = updatedImages.map((img) => img.preview);
                  const filteredUrls = urls.filter((url) => !url.startsWith("blob:"));
                  const valueToSend = multiple ? filteredUrls : filteredUrls[0] || "";

                  // Schedule onChange to be called after state update
                  pendingOnChangeRef.current = valueToSend;

                  return updatedImages;
                });
              })
              .catch((error) => {
                console.error("Upload failed:", error);
                // On error, remove the failed images
                setImages((prevImages) =>
                  prevImages.filter(
                    (img) => !filesToAdd.find((f) => f.id === img.id)
                  )
                );
              })
              .finally(() => {
                setUploading(false);
              });
          } else {
            // Just update previews without upload
            const urls = limitedImages.map((img) => img.preview);
            const valueToSend = multiple ? urls : urls[0] || "";

            pendingOnChangeRef.current = valueToSend;
          }

          return limitedImages;
        });
      }
    },
    [multiple, maxFiles, onUpload, validateFile]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      processFiles(acceptedFiles);
    },
    [processFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    multiple,
    disabled,
    maxSize,
  });

  const removeImage = (id: string) => {
    const imageToRemove = images.find((img) => img.id === id);
    if (imageToRemove) {
      // Clean up object URL if it's a blob URL to prevent memory leaks
      if (imageToRemove.preview.startsWith("blob:")) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
    }
    const newImages = images.filter((img) => img.id !== id);
    setImages(newImages);
    const urls = newImages.map((img) => img.preview);
    // Filter blob URLs if onUpload is present to avoid syncing incomplete state
    const filteredUrls = onUpload
      ? urls.filter((url) => !url.startsWith("blob:"))
      : urls;
    const valueToSend = multiple ? filteredUrls : filteredUrls[0] || "";

    // Schedule onChange to be called after state update
    pendingOnChangeRef.current = valueToSend;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    processFiles(files);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}

      {/* Drop zone */}
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-gray-300 hover:border-gray-400",
          disabled && "opacity-50 cursor-not-allowed",
          error && "border-red-500"
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center space-y-2">
          <Upload className="h-8 w-8 text-gray-400" />
          <div className="text-sm text-gray-600">
            {isDragActive ? (
              "Drop images here..."
            ) : (
              <>
                Drag & drop images here, or{" "}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  browse
                </button>
              </>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {acceptedTypes.join(", ").toUpperCase()} • Max{" "}
            {Math.round(maxSize / 1024 / 1024)}MB each
            {multiple && ` • Up to ${maxFiles} files`}
          </div>
        </div>
      </div>

      {/* Hidden file input for manual selection */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={acceptedTypes.join(",")}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Image previews */}
      {images.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image) => (
              <div key={image.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden border bg-gray-50">
                  <img
                    src={image.preview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  {uploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="text-white text-sm">Uploading...</div>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeImage(image.id)}
                  disabled={disabled || uploading}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Error message */}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Alternative: URL input for existing URLs */}
      <div className="space-y-2">
        <Label className="text-xs text-gray-500">Or enter image URL(s)</Label>
        {multiple ? (
          <div className="space-y-2">
            {(Array.isArray(effectiveValue) ? effectiveValue : []).map(
              (url, index) => (
                <input
                  key={index}
                  type="url"
                  value={typeof url === "string" ? url : ""}
                  onChange={(e) => {
                    const currentUrls = Array.isArray(effectiveValue)
                      ? effectiveValue
                      : [];
                    const newUrls = [...currentUrls];
                    newUrls[index] = e.target.value;
                    // Schedule onChange to be called after state update
                    pendingOnChangeRef.current = newUrls;
                    // Update images immediately for visual feedback
                    const newImages = createImageObjects(
                      newUrls.filter(
                        (url) =>
                          url && typeof url === "string" && url.trim() !== ""
                      )
                    );
                    setImages(newImages);
                  }}
                  placeholder="https://example.com/image.jpg"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={disabled}
                />
              )
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const currentUrls = Array.isArray(effectiveValue)
                  ? effectiveValue
                  : [];
                const newUrls = [...currentUrls, ""];
                // Schedule onChange to be called after state update
                pendingOnChangeRef.current = newUrls;
                // Update images immediately for visual feedback
                const newImages = createImageObjects(
                  newUrls.filter(
                    (url) => url && typeof url === "string" && url.trim() !== ""
                  )
                );
                setImages(newImages);
              }}
            >
              Add URL
            </Button>
          </div>
        ) : (
          <input
            type="url"
            value={typeof effectiveValue === "string" ? effectiveValue : ""}
            onChange={(e) => {
              const newValue = e.target.value;
              // Schedule onChange to be called after state update
              pendingOnChangeRef.current = newValue;
              // Update images immediately for visual feedback
              if (newValue && newValue.trim() !== "") {
                const newImages = createImageObjects([newValue]);
                setImages(newImages);
              } else {
                setImages([]);
              }
            }}
            placeholder="https://example.com/image.jpg"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
